/* eslint-disable max-len */
import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { useDraft } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { BoardData } from '../../../api/ApiTypes';
import { Client, Communicator } from '../../../concepts/objects';
import { ClipDataBuilder, ClipboardService } from '../../../platform/Clipboard';
import { useLoader, useSubmitter } from '../../../platform/Hook';
import { Position } from '../../../ui/Position';
import { ResultInfoForSubmitter } from '../../../ui/ResultInfo';
import { HelpTip } from '../../../ui/Tip';
import { useResolve } from '../../Container';
import { AccountModel } from '../../account/AccountModel';
import { EnableIfHasFeature, HideIfHasFeature, ShowIfHasFeature } from '../../common/FeatureElements';
import { DeprecatedSpinner } from '../../common/Spinner';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { PopupManager } from '../../popups/PopupManager';
import { UpgradeTextButton } from '../../upgrade/UpgradeButton';
import { BoardInfo } from '../BoardInfo';
import { EditorSession } from '../EditorSession';
import { Button, Colors, Input, Select, SelectProps } from './../../../ui';
import { manageAccessPopup } from './ManageAccessPopup';

type SharingRule = BoardData['viewers'];
type SharingOption = { key: SharingRule, label: string, disabled?: boolean };
type SharingRuleMap = {
    viewers: SharingRule,
    joiners: SharingRule,
};

export const JoinersRuleOption = {
    all: (account?: { label: string }): SharingOption[] => [
        {
            key: 'workspace',
            label: 'only members with workspace access',
        },
        {
            key: 'account',
            label: `all members of ${account?.label}`,
        },
        {
            key: 'world',
            label: 'anyone with the link',
        },
    ],
};

export const ViewersRuleOption = {
    all: (account?: { label: string }): SharingOption[] => [
        {
            key: 'workspace',
            label: 'only members with workspace access',
        },
        {
            key: 'account',
            label: `all members of ${account?.label}`,
        },
        {
            key: 'world',
            label: 'anyone with the link',
        },
    ],
};

const enforceJoinerViewerRestrictions = ({ joiners, viewers }: SharingRuleMap) => {
    const jl = JoinersRuleOption.all().findIndex(x => x.key === joiners);
    const vl = ViewersRuleOption.all().findIndex(x => x.key === viewers);
    // Viewers must be at least as permissive as joiners
    if (vl < jl) {
        viewers = joiners;
    }
    return { joiners, viewers };
};

const LiveGuestSlotInfo = ({ boardId }: { boardId: string }) => {

    const api = useResolve(ApiClient);
    const [loading, data] = useLoader(() => api.exec(v2.boards.info(boardId, 'guests')), []);

    return <Copy size="1">
        <Flexer flex="row" center>
            {loading ? <DeprecatedSpinner /> : <>
                {data?.guests?.slots < 0 && <span>{data?.guests?.count} guest slots in use.</span>}
                {data?.guests?.slots >= 0 && <span>{data?.guests?.count} / {data?.guests?.slots} guest slots in use.</span>}
            </>}
        </Flexer>
    </Copy>;
};

const Layout = styled.div`
    ${Copy} {
        margin: 0;
    }
    ${Select.Frame} {
        width: 100%;
    }
`;

const LayoutRow = styled.div`
    display: flex;
    padding: 1rem 1.5rem;
    border-top: solid 1px ${Colors.border};
`;

const LayoutCell = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;

    &:first-child {
        flex: 0 0 15rem;
    }
`;

const ShareOptionSelect: LFC<Omit<SelectProps<SharingOption>, 'renderItems'>> = (props) => {
    return <Select {...props} renderItems={
        (items, selectedItem, onSelect) => items.map(tm =>
            <Select.MenuItem key={tm.key} disabled={tm.disabled} onClick={tm.disabled ? null : onSelect(tm) as any}>
                <Select.MenuItemText>{tm.label}</Select.MenuItemText>
                {tm === selectedItem && <Select.MenuItemCheck />}
            </Select.MenuItem>
        )
    } />;
};

const AccessView = ({ onDismiss }: { onDismiss: VoidCallback }) => {

    const account = useResolve(AccountModel);
    const api = useResolve(ApiClient);
    const clipboard = useResolve(ClipboardService);
    const comms = useResolve(Communicator);
    const popups = useResolve(PopupManager);
    const session = useResolve(EditorSession);
    const board = session.get(BoardInfo);

    const joinersOptions = useMemo(() => JoinersRuleOption.all(account), [account]);
    const viewersOptions = useMemo(() => ViewersRuleOption.all(account), [account]);

    const [rules, patchRules] = useDraft({
        joiners: !!account.plan.features.accessControl ? board.joiners : 'world',
        viewers: !!account.plan.features.accessControl ? board.viewers : 'world',
    });

    const [copied, setCopied] = useState(false);

    const [onSubmitRules, working, result] = useSubmitter(async (values: SharingRuleMap) => {
        session.patch(BoardInfo, values);
        await api.exec(v2.boards.setSharing({ boardId: board.id, ...values }));
        comms.send('sharing', values);
    }, [board.id]);

    const onPatchRules = useCallback((patch: Partial<SharingRuleMap>) => {
        const merged = { ...rules, ...patch };
        const validated = enforceJoinerViewerRestrictions({ ...rules, ...patch });
        patchRules(validated);
        onSubmitRules(validated);
    }, [rules]);

    const onManageAccess = useCallback(() => {
        popups.invoke(manageAccessPopup());
        onDismiss();
    }, [onDismiss]);

    const onCopyLink = useCallback(() => {
        const clip = new ClipDataBuilder();
        clip.setText(window.location.href);
        clipboard.write(clip.build());
        setCopied(true);
    }, []);

    useEffectOnce(() => comms.recv('sharing', (_sender: Client, x: SharingRuleMap) => patchRules(x)));

    return <Flexer id='share-view' width="40">
        <Flexer padding="1" background={Colors.gray50}>
            <Flexer flex="row">
                <Flexer relative grow margin="0 .25 0 0" background="white">
                    <Input value={window.location.href} />
                    {copied && <Position inset=".5" at="right">
                        <Title size="sm" flush color={Colors.positive}>Copied!</Title>
                    </Position>}
                </Flexer>
                <Button size="md" variant="primary" onAction={onCopyLink}>
                    Copy Link
                </Button>
            </Flexer>
            <Flexer padding=".5 0 0">
                <Copy flush size=".75">
                    Share this link to invite people to the board.&nbsp;
                    <ShowIfHasFeature name="accessControl">
                        You will be asked to approve anyone joining to edit from outside your organization.
                        {/* For more information <a href="#">review our documentation here</a>. */}
                    </ShowIfHasFeature>
                </Copy>
            </Flexer>
        </Flexer>
        <Layout>
            <EnableIfHasFeature name="accessControl">
                <LayoutRow>
                    <LayoutCell>
                        <Copy flush size="1">
                            Can be joined by
                            <HelpTip content="Choose who can instantly join and edit the board.  Everyone else will be added to the waiting room for approval by a team member." />
                        </Copy>

                    </LayoutCell>
                    <LayoutCell id="join-access">
                        <ShareOptionSelect
                            items={joinersOptions}
                            selected={joinersOptions.find(x => x.key === rules.joiners)}
                            onChange={x => onPatchRules({ joiners: x.key })} />
                    </LayoutCell>
                </LayoutRow>
                <LayoutRow>
                    <LayoutCell>
                        <Copy size="1">
                            Can be viewed by
                            <HelpTip content={'Choose who view the board.  This setting must be at least as permissive as the "joined by" setting above.'} />
                        </Copy>
                    </LayoutCell>
                    <LayoutCell>
                        <ShareOptionSelect
                            items={viewersOptions}
                            selected={viewersOptions.find(x => x.key === rules.viewers)}
                            onChange={x => onPatchRules({ viewers: x.key })} />
                    </LayoutCell>
                </LayoutRow>
            </EnableIfHasFeature>
            <ShowIfHasFeature name="accessControl">
                <LayoutRow>
                    <LayoutCell>
                        <LiveGuestSlotInfo boardId={board.id} />
                    </LayoutCell>
                    <LayoutCell>
                        <Button onAction={onManageAccess}>Manage Access</Button>
                    </LayoutCell>
                    <LayoutCell>
                        {/* <Button onAction={onScheduling}>Scheduling</Button>*/}
                    </LayoutCell>
                </LayoutRow>
            </ShowIfHasFeature>
            <HideIfHasFeature name="accessControl">
                <LayoutRow>
                    <Copy size="1">
                        For guest users and better access
                        control <UpgradeTextButton>upgrade your space.</UpgradeTextButton>
                    </Copy>
                </LayoutRow>
            </HideIfHasFeature>
        </Layout>
        <ResultInfoForSubmitter padding="1" result={result} />
    </Flexer >;
};

export const ShareView = ({ onDismiss }: { onDismiss: VoidCallback }) => {

    // const [tab, setTab] = useState(1);

    // return <Flexer>
    //     <PopupTabs padding="1 1 0 1" spacing="1">
    //         <PopupTabs.Item onClick={() => setTab(0)} selected={tab === 0}>Access Control</PopupTabs.Item>
    //         <PopupTabs.Item onClick={() => setTab(1)} selected={tab === 1}>Scheduling</PopupTabs.Item>
    //     </PopupTabs>
    //     {tab === 0 && <AccessView onDismiss={onDismiss} />}
    //     {tab === 1 && <SchedulingView />}
    // </Flexer>;

    return <Flexer>
        <AccessView onDismiss={onDismiss} />
    </Flexer>;
};
